import { Fragment, useMemo, useState, type KeyboardEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/authStore'
import { InlineError } from '../components/InlineError'
import { BrandCombobox, type PoBrand } from '../components/BrandCombobox'
import '../styles/ui.css'
import '../styles/purchase-orders.css'

// ── API types — everything comes from the legacy PO system ───────────
type PoCatalogSummary = { catalogGuid: string; season: string | null; itemCount: number }
type PoCatalogItem = {
    catalogItemGuid: string; itemCardId: number | null; styleCode: string | null; prodNo: string | null
    description: string; unitCost: number | null; retail: number | null
}
type PoCatalogDetail = { catalogGuid: string; season: string | null; items: PoCatalogItem[] }

type ExistingPo = {
    poGuid: string; poNumber: string | null; shipDate: string | null
    storeGuid: string | null; storeNumber: string | null; storeName: string | null
    lineCount: number; units: number; revisionNumber: number
}
type PoDetail = {
    header: ExistingPo & { catalogGuid: string | null }
    lines: { catalogItemGuid: string | null; itemCardId: number | null; colorId: number; sizeId: number; quantity: number }[]
}

type VariantAxes = {
    itemCardId: number
    colors: { colorId: number; name: string | null }[]
    sizes: { sizeId: number; name: string | null }[]
    onHand: Record<string, number>
}
type PoStore = { storeId: string; storeNumber: string; name: string; city: string | null }

const storeLabel = (s: PoStore | undefined | null) =>
    s ? [s.storeNumber, s.name].filter(Boolean).join(' · ') : ''

type PoMode = 'new' | 'revise'

// ── Suggestion factors (docs/PurchaseOrderMechanics.md §16) ──────────
// Order = priority: the engine will weigh the top of the list most heavily.
type Factor = { key: string; label: string; hint: string; enabled: boolean; available: boolean }

const DEFAULT_FACTORS: Factor[] = [
    { key: 'ly-booking', label: "Last year's booking vs inventory", enabled: true, available: true,
      hint: 'Compares the prior season’s booking against on-hand and sell-through velocity.' },
    { key: 'stockouts', label: 'Early stock-outs & lost sales', enabled: true, available: true,
      hint: 'Styles that sold out before season end — the strongest under-buy signal.' },
    { key: 'never-out', label: 'Chronic Never Out breaches', enabled: true, available: true,
      hint: 'SKUs that repeatedly fall below their minimum are being under-bought.' },
    { key: 'size-curve', label: 'Size-curve optimisation', enabled: true, available: true,
      hint: 'Reshapes quantities across the size run from per-size sell-through.' },
    { key: 'new-skus', label: 'Brand-new SKUs & colours', enabled: true, available: true,
      hint: 'Styles and colours appearing in this season’s catalog for the first time.' },
    { key: 'eol-colors', label: 'EOL colour replacements', enabled: true, available: true,
      hint: 'Discontinued colours matched to their nearest successor in the new season.' },
    { key: 'eol-items', label: 'EOL item replacements', enabled: true, available: true,
      hint: 'Dropped styles matched to a successor by code lineage and description.' },
    { key: 'margin', label: 'Margin & cost movement', enabled: false, available: true,
      hint: 'Flags styles whose cost rose faster than retail since last season.' },
    { key: 'markdown', label: 'Markdown & clearance history', enabled: false, available: true,
      hint: 'Deep prior-season discounting as an over-buy signal.' },
    { key: 'otb', label: 'Open-to-buy ceiling', enabled: false, available: true,
      hint: 'Caps the suggested total against a season budget.' },
    { key: 'packs', label: 'Pack / minimum-order multiples', enabled: false, available: true,
      hint: 'Rounds suggestions to supplier pack sizes. No pack data is modelled yet.' },
    { key: 'search-trends', label: 'Local search trends (demand gap)', enabled: false, available: false,
      hint: 'External demand signal. Deferred — no trends data source is connected.' },
]

type BaselineOptions = {
    growthPercent: number
    deFactorMultibuys: boolean
    historyMonths: number
}

const DEFAULT_BASELINE: BaselineOptions = { growthPercent: 0, deFactorMultibuys: true, historyMonths: 12 }

const MAX_RENDERED_ROWS = 500
// Real orders run to 11+ delivery dates (docs/PurchaseOrderMechanics.md §4).
const MAX_DELIVERIES = 12

const money = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' })
const fmtMoney = (v: number | null | undefined) => (v == null ? '—' : money.format(v))
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—')
const catalogLabel = (season: string | null) => season?.trim() || '(no season label)'
const variantKey = (colorId: number, sizeId: number) => `${colorId}|${sizeId}`

// itemGuid -> delivery index -> "colorId|sizeId" -> quantity
type QtyMap = Record<string, Record<number, Record<string, number>>>

const sumRecord = (r: Record<string, number> | undefined) =>
    r ? Object.values(r).reduce((a, b) => a + (b || 0), 0) : 0
const itemDeliveryUnits = (qtys: QtyMap, itemGuid: string, di: number) => sumRecord(qtys[itemGuid]?.[di])
const itemUnits = (qtys: QtyMap, itemGuid: string) =>
    Object.values(qtys[itemGuid] ?? {}).reduce((a, d) => a + sumRecord(d), 0)

/** `embedded`: rendered as BOSS MultiPO's Planner mode, which draws the page header itself. */
export default function PurchaseOrderPlanningPage({ embedded = false }: { embedded?: boolean }) {
    const token = useAuthStore(s => s.accessToken)
    const qc = useQueryClient()

    const [brandId, setBrandId] = useState('')
    const [catalogId, setCatalogId] = useState('')
    const [mode, setMode] = useState<PoMode | null>(null)
    const [storeId, setStoreId] = useState('')
    const [selectedPoGuid, setSelectedPoGuid] = useState('')
    const [baseline, setBaseline] = useState<BaselineOptions>(DEFAULT_BASELINE)
    const [factors, setFactors] = useState<Factor[]>(DEFAULT_FACTORS)
    const [deliveries, setDeliveries] = useState<string[]>([''])
    const [qtys, setQtys] = useState<QtyMap>({})
    const [expanded, setExpanded] = useState<Record<string, boolean>>({})
    const [filter, setFilter] = useState('')

    const storesQuery = useQuery<PoStore[]>({
        queryKey: ['po-stores'],
        queryFn: () => api('/api/purchase-orders/stores', token),
    })
    const brandsQuery = useQuery<PoBrand[]>({
        queryKey: ['po-brands'],
        queryFn: () => api('/api/purchase-orders/brands', token),
    })
    const catalogsQuery = useQuery<PoCatalogSummary[]>({
        queryKey: ['po-catalogs', brandId],
        queryFn: () => api(`/api/purchase-orders/catalogs?brandId=${brandId}`, token),
        enabled: !!brandId,
    })
    const catalogQuery = useQuery<PoCatalogDetail>({
        queryKey: ['po-catalog', catalogId],
        queryFn: () => api(`/api/purchase-orders/catalogs/${catalogId}`, token),
        enabled: !!catalogId,
    })
    const posQuery = useQuery<ExistingPo[]>({
        queryKey: ['po-existing', catalogId],
        queryFn: () => api(`/api/purchase-orders/pos?catalogGuid=${catalogId}`, token),
        enabled: !!catalogId && mode === 'revise',
    })
    const poQuery = useQuery<PoDetail>({
        queryKey: ['po-detail', selectedPoGuid],
        queryFn: () => api(`/api/purchase-orders/pos/${selectedPoGuid}`, token),
        enabled: !!selectedPoGuid,
    })

    const brands = brandsQuery.data ?? []
    const catalogs = catalogsQuery.data ?? []
    const catalog = catalogQuery.data
    const existingPos = posQuery.data ?? []
    const stores = storesQuery.data ?? []
    const selectedBrand = brands.find(b => b.id === brandId)
    const loadedPo = selectedPoGuid ? poQuery.data : undefined
    // In revision mode the store comes from the PO itself — it isn't the buyer's to change.
    const effectiveStoreId = (mode === 'revise' ? (loadedPo?.header.storeGuid ?? '') : storeId) || ''
    const selectedStore = stores.find(s => s.storeId.toLowerCase() === effectiveStoreId.toLowerCase())

    const resetPlan = () => {
        setQtys({})
        setExpanded({})
        setFilter('')
        setDeliveries([''])
    }
    const selectBrand = (id: string) => {
        setBrandId(id); setCatalogId(''); setMode(null); setSelectedPoGuid(''); resetPlan()
    }
    const selectCatalog = (id: string) => {
        setCatalogId(id); setMode(null); setSelectedPoGuid(''); resetPlan()
    }
    const selectMode = (m: PoMode) => {
        setMode(m); setSelectedPoGuid(''); resetPlan()
        // A new PO needs a store chosen deliberately; a revision inherits the PO's.
        if (m === 'revise') setStoreId('')
    }

    // Loading an existing PO seeds the grid from its lines. A legacy PO is one store on one
    // ship date, so revising it edits exactly one delivery (mechanics doc §4).
    const loadPo = async (po: ExistingPo) => {
        setSelectedPoGuid(po.poGuid)
        // Seed the cache the header query reads from, so this is a single fetch.
        const detail = await qc.fetchQuery<PoDetail>({
            queryKey: ['po-detail', po.poGuid],
            queryFn: () => api(`/api/purchase-orders/pos/${po.poGuid}`, token),
        })
        const next: QtyMap = {}
        for (const line of detail.lines) {
            if (!line.catalogItemGuid || !line.quantity) continue
            const item = (next[line.catalogItemGuid] ??= {})
            const delivery = (item[0] ??= {})
            delivery[variantKey(line.colorId, line.sizeId)] = line.quantity
        }
        setQtys(next)
        setExpanded({})
        setFilter('')
        setDeliveries([po.shipDate ? po.shipDate.slice(0, 10) : ''])
    }

    const setQty = (itemGuid: string, di: number, key: string, value: number) => {
        setQtys(prev => {
            const item = { ...(prev[itemGuid] ?? {}) }
            const delivery = { ...(item[di] ?? {}) }
            if (value > 0) delivery[key] = value
            else delete delivery[key]
            item[di] = delivery
            return { ...prev, [itemGuid]: item }
        })
    }

    const filteredItems = useMemo(() => {
        const items = catalog?.items ?? []
        const q = filter.trim().toLowerCase()
        if (!q) return items
        return items.filter(i =>
            i.description.toLowerCase().includes(q)
            || (i.styleCode ?? '').toLowerCase().includes(q)
            || (i.prodNo ?? '').toLowerCase().includes(q))
    }, [catalog, filter])
    const renderedItems = filteredItems.slice(0, MAX_RENDERED_ROWS)

    const totals = useMemo(() => {
        let units = 0
        let value = 0
        for (const item of catalog?.items ?? []) {
            const u = itemUnits(qtys, item.catalogItemGuid)
            units += u
            value += u * (item.unitCost ?? 0)
        }
        return { units, value }
    }, [catalog, qtys])

    const isRevision = mode === 'revise'
    // A PO is always for exactly one store, so a new PO can't be planned until one is chosen.
    const planReady = !!catalog && ((mode === 'new' && !!storeId) || (isRevision && !!selectedPoGuid))

    const addDelivery = () => setDeliveries(d => [...d, ''])
    const setDeliveryDate = (idx: number, date: string) =>
        setDeliveries(d => d.map((v, i) => (i === idx ? date : v)))
    const removeDelivery = (idx: number) => {
        setDeliveries(d => d.filter((_, i) => i !== idx))
        // Keep the per-delivery quantity maps index-aligned with the delivery list.
        setQtys(prev => {
            const next: QtyMap = {}
            for (const [itemGuid, byDelivery] of Object.entries(prev)) {
                const shifted: Record<number, Record<string, number>> = {}
                for (const [diRaw, cells] of Object.entries(byDelivery)) {
                    const di = Number(diRaw)
                    if (di === idx) continue
                    shifted[di > idx ? di - 1 : di] = cells
                }
                next[itemGuid] = shifted
            }
            return next
        })
    }

    return (
        <>
            {!embedded && (
                <div className="page-header">
                    <h1>PO Planning</h1>
                    <p>Write purchase orders from a brand&rsquo;s season catalog. Quantities are entered per colour and size, the way the order is actually placed.</p>
                </div>
            )}

            {brandsQuery.isError && <InlineError message="Failed to load brands from the legacy database." style={{ marginBottom: 'var(--space-4)' }} />}

            <div className="po-panel po-toolbar-panel">
                <div className="po-toolbar">
                    <div className="form-field">
                        <label className="form-label">Brand</label>
                        <BrandCombobox
                            brands={brands}
                            loading={brandsQuery.isLoading}
                            value={selectedBrand ?? null}
                            onSelect={b => selectBrand(b?.id ?? '')}
                        />
                    </div>
                    <div className="form-field">
                        <label className="form-label">Catalog</label>
                        <select className="form-input" value={catalogId} onChange={e => selectCatalog(e.target.value)} disabled={!brandId}>
                            <option value="">
                                {!brandId ? 'Select a brand first'
                                    : catalogsQuery.isLoading ? 'Loading catalogs…'
                                    : catalogs.length === 0 ? 'No season catalogs'
                                    : 'Select a catalog…'}
                            </option>
                            {catalogs.map(c => (
                                <option key={c.catalogGuid} value={c.catalogGuid}>
                                    {catalogLabel(c.season)} — {c.itemCount} styles
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {!brandId ? (
                <div className="empty-state"><p>Select a brand to begin planning a purchase order.</p></div>
            ) : !catalogId ? (
                catalogsQuery.isLoading ? (
                    <div className="empty-state"><p>Loading catalogs…</p></div>
                ) : catalogsQuery.isError ? (
                    <div className="empty-state"><p>Failed to load this brand&rsquo;s season catalogs from the legacy database.</p></div>
                ) : catalogs.length === 0 ? (
                    <div className="empty-state">
                        <p>No season catalogs exist for {selectedBrand?.name ?? 'this brand'}.</p>
                        <p className="po-truncation-note">Catalogs come from the legacy PO system and are maintained there.</p>
                    </div>
                ) : (
                    <div className="empty-state"><p>Select a catalog to start writing the PO.</p></div>
                )
            ) : (
                <>
                    <div className="po-panel po-toolbar-panel">
                        <div className="po-modes">
                            <button
                                type="button"
                                className={`po-mode${mode === 'new' ? ' selected' : ''}`}
                                onClick={() => selectMode('new')}
                            >
                                <span className="po-mode-icon">➕</span>
                                <span>
                                    <span className="po-mode-label">Write a new PO</span>
                                    <span className="po-mode-hint">Start an empty order against this catalog, split across one or more delivery dates.</span>
                                </span>
                            </button>
                            <button
                                type="button"
                                className={`po-mode${mode === 'revise' ? ' selected' : ''}`}
                                onClick={() => selectMode('revise')}
                            >
                                <span className="po-mode-icon">✎</span>
                                <span>
                                    <span className="po-mode-label">Revise an existing PO</span>
                                    <span className="po-mode-hint">Pick a PO already written against this catalog and adjust its quantities.</span>
                                </span>
                            </button>
                        </div>

                        {mode === 'new' && (
                            <div className="po-toolbar" style={{ marginTop: 'var(--space-4)' }}>
                                <div className="form-field">
                                    <label className="form-label">Store</label>
                                    <select
                                        className="form-input"
                                        value={storeId}
                                        onChange={e => { setStoreId(e.target.value); resetPlan() }}
                                    >
                                        <option value="">
                                            {storesQuery.isLoading ? 'Loading stores…'
                                                : stores.length === 0 ? 'No stores available to you'
                                                : 'Select a store…'}
                                        </option>
                                        {stores.map(s => (
                                            <option key={s.storeId} value={s.storeId}>{storeLabel(s)}</option>
                                        ))}
                                    </select>
                                    <p className="po-field-hint">
                                        A purchase order is written for one store. Only stores you have access to are listed.
                                    </p>
                                </div>
                            </div>
                        )}

                        {isRevision && loadedPo && (
                            <div className="po-context-bar" style={{ marginTop: 'var(--space-4)' }}>
                                <span>Revising <strong>{loadedPo.header.poNumber ?? '(no number)'}</strong></span>
                                <span className="badge badge-approved">Revision {(loadedPo.header.revisionNumber ?? 0) + 1}</span>
                                <span>{loadedPo.header.storeNumber ? `Store ${loadedPo.header.storeNumber}` : ''} {loadedPo.header.storeName}</span>
                                <span>Ship {fmtDate(loadedPo.header.shipDate)}</span>
                                <button className="btn btn-secondary" onClick={() => { setSelectedPoGuid(''); resetPlan() }}>Choose a different PO</button>
                            </div>
                        )}
                    </div>

                    {isRevision && !selectedPoGuid && (
                        <div className="po-panel" style={{ marginBottom: 'var(--space-4)' }}>
                            <h2 className="po-builder-title" style={{ marginBottom: 'var(--space-3)' }}>Existing POs on this catalog</h2>
                            {posQuery.isLoading ? (
                                <div className="empty-state"><p>Loading purchase orders…</p></div>
                            ) : posQuery.isError ? (
                                <InlineError message="Failed to load existing purchase orders." />
                            ) : existingPos.length === 0 ? (
                                <div className="empty-state">
                                    <p>No purchase orders have been written against this catalog yet.</p>
                                    <p className="po-truncation-note">Only POs for stores you can access are listed.</p>
                                </div>
                            ) : (
                                <div className="data-table-wrapper">
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>PO number</th>
                                                <th>Store</th>
                                                <th>Ship date</th>
                                                <th className="po-num">Lines</th>
                                                <th className="po-num">Units</th>
                                                <th className="po-num">Revision</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {existingPos.map(po => (
                                                <tr
                                                    key={po.poGuid}
                                                    className="po-list-row"
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-label={`Revise purchase order ${po.poNumber ?? po.poGuid}`}
                                                    onClick={() => loadPo(po)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); loadPo(po) }
                                                    }}
                                                >
                                                    <td className="po-style">{po.poNumber ?? '(no number)'}</td>
                                                    <td>{[po.storeNumber, po.storeName].filter(Boolean).join(' · ') || '—'}</td>
                                                    <td>{fmtDate(po.shipDate)}</td>
                                                    <td className="po-num">{po.lineCount.toLocaleString()}</td>
                                                    <td className="po-num">{po.units.toLocaleString()}</td>
                                                    <td className="po-num">{po.revisionNumber || '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}

                    {!mode ? (
                        <div className="empty-state"><p>Choose whether to write a new PO or revise an existing one.</p></div>
                    ) : mode === 'new' && !storeId ? (
                        <div className="empty-state">
                            <p>{stores.length === 0 && !storesQuery.isLoading
                                ? 'You do not have access to any stores, so a purchase order cannot be written.'
                                : 'Select the store this purchase order is for.'}</p>
                        </div>
                    ) : catalogQuery.isLoading ? (
                        <div className="empty-state"><p>Loading catalog…</p></div>
                    ) : !catalog ? (
                        <div className="empty-state"><p>Catalog not found.</p></div>
                    ) : !planReady ? null : (
                        <div className="po-layout">
                            <div className="po-panel po-builder">
                                <div className="po-builder-header">
                                    <div>
                                        <h2 className="po-builder-title">{catalogLabel(catalog.season)}</h2>
                                        <div className="po-builder-sub">
                                            <span>{selectedBrand?.name}</span>
                                            <span>· {catalog.items.length.toLocaleString()} styles</span>
                                            {selectedStore && <span className="badge badge-active">{storeLabel(selectedStore)}</span>}
                                            {isRevision && <span className="badge badge-pending">Revision</span>}
                                        </div>
                                    </div>
                                    <div className="po-builder-actions">
                                        <input
                                            className="form-input"
                                            placeholder="Filter styles…"
                                            value={filter}
                                            onChange={e => setFilter(e.target.value)}
                                            style={{ width: 200 }}
                                        />
                                        <button className="btn btn-primary" disabled title="Saving is coming soon">
                                            {isRevision ? 'Save revision' : 'Save draft'}
                                        </button>
                                    </div>
                                </div>

                                {catalog.items.length === 0 ? (
                                    <div className="empty-state">
                                        <p>This season catalog has no items in the legacy PO system.</p>
                                    </div>
                                ) : (
                                    <>
                                        <div className="data-table-wrapper po-table-wrap">
                                            <table className="data-table">
                                                <thead>
                                                    <tr>
                                                        <th style={{ width: 28 }}></th>
                                                        <th>Style</th>
                                                        <th>Description</th>
                                                        <th className="po-num">Cost</th>
                                                        <th className="po-num">Retail</th>
                                                        {deliveries.map((d, i) => (
                                                            <th key={i} className="po-num">
                                                                {deliveries.length === 1 ? 'Qty' : `D${i + 1}`}
                                                                {d && <div style={{ fontWeight: 400, fontSize: 10 }}>{fmtDate(d)}</div>}
                                                            </th>
                                                        ))}
                                                        <th className="po-num">Units</th>
                                                        <th className="po-num">Value</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {renderedItems.map((item, itemIdx) => {
                                                        const units = itemUnits(qtys, item.catalogItemGuid)
                                                        const isOpen = !!expanded[item.catalogItemGuid]
                                                        const colSpan = 7 + deliveries.length
                                                        return (
                                                            <Fragment key={item.catalogItemGuid}>
                                                                <tr className={`po-item-row${isOpen ? ' expanded' : ''}`}>
                                                                    <td>
                                                                        <button
                                                                            className="po-expand"
                                                                            title={isOpen ? 'Hide colour/size grid' : 'Show colour/size grid'}
                                                                            aria-expanded={isOpen}
                                                                            onClick={() => setExpanded(e => ({ ...e, [item.catalogItemGuid]: !isOpen }))}
                                                                        >
                                                                            {isOpen ? '▾' : '▸'}
                                                                        </button>
                                                                    </td>
                                                                    <td className="po-style">{item.styleCode ?? item.prodNo ?? '—'}</td>
                                                                    <td>{item.description}</td>
                                                                    <td className="po-num">{fmtMoney(item.unitCost)}</td>
                                                                    <td className="po-num">{fmtMoney(item.retail)}</td>
                                                                    {deliveries.map((_, di) => {
                                                                        const u = itemDeliveryUnits(qtys, item.catalogItemGuid, di)
                                                                        return <td key={di} className="po-num">{u ? u.toLocaleString() : '—'}</td>
                                                                    })}
                                                                    <td className="po-num">{units ? units.toLocaleString() : '—'}</td>
                                                                    <td className="po-num">{units ? fmtMoney(units * (item.unitCost ?? 0)) : '—'}</td>
                                                                </tr>
                                                                {isOpen && (
                                                                    <tr>
                                                                        <td className="po-grid-cell" colSpan={colSpan}>
                                                                            <ItemVariantGrid
                                                                                item={item}
                                                                                itemIdx={itemIdx}
                                                                                deliveries={deliveries}
                                                                                storeId={effectiveStoreId}
                                                                                qtys={qtys[item.catalogItemGuid] ?? {}}
                                                                                onQty={(di, key, value) => setQty(item.catalogItemGuid, di, key, value)}
                                                                                token={token}
                                                                            />
                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </Fragment>
                                                        )
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                        {filteredItems.length > MAX_RENDERED_ROWS && (
                                            <p className="po-truncation-note">
                                                Showing the first {MAX_RENDERED_ROWS} of {filteredItems.length.toLocaleString()} styles — use the filter to narrow down.
                                            </p>
                                        )}
                                        <div className="po-totals">
                                            <span><strong>{totals.units.toLocaleString()}</strong> units</span>
                                            <span><strong>{fmtMoney(totals.value)}</strong> at cost</span>
                                        </div>
                                    </>
                                )}
                            </div>

                            <aside className="po-panel po-options">
                                <h3 className="po-options-title">Suggestion settings</h3>
                                <p className="po-options-hint">
                                    These tailor the assisted PO suggestions. The engine isn&rsquo;t wired up yet — the controls scaffold the workflow.
                                </p>

                                <div className="form-field">
                                    <label className="form-label">Growth factor (%)</label>
                                    <input
                                        type="number"
                                        step={5}
                                        className="form-input"
                                        value={baseline.growthPercent}
                                        onChange={e => setBaseline(o => ({ ...o, growthPercent: Number(e.target.value) || 0 }))}
                                    />
                                    <p className="po-field-hint">Uplift (or cut, if negative) applied to the sales baseline.</p>
                                </div>

                                <div className="form-field">
                                    <label className="form-label">Sales history window</label>
                                    <select
                                        className="form-input"
                                        value={baseline.historyMonths}
                                        onChange={e => setBaseline(o => ({ ...o, historyMonths: Number(e.target.value) }))}
                                    >
                                        <option value={3}>Last 3 months</option>
                                        <option value={6}>Last 6 months</option>
                                        <option value={12}>Last 12 months</option>
                                        <option value={24}>Last 24 months</option>
                                    </select>
                                    <p className="po-field-hint">How far back the sales baseline looks.</p>
                                </div>

                                <div className="po-toggle-row">
                                    <div>
                                        <div className="po-toggle-label">De-factor multibuys</div>
                                        <div className="po-toggle-hint">Strip multibuy-promo transactions from the baseline so bundle spikes don&rsquo;t inflate suggestions.</div>
                                    </div>
                                    <label className="po-toggle">
                                        <input
                                            type="checkbox"
                                            checked={baseline.deFactorMultibuys}
                                            onChange={e => setBaseline(o => ({ ...o, deFactorMultibuys: e.target.checked }))}
                                        />
                                        <span className="po-toggle-slider" />
                                    </label>
                                </div>

                                <FactorList factors={factors} onChange={setFactors} />

                                <div className="po-section-title">Delivery schedule</div>
                                {isRevision ? (
                                    <p className="po-field-hint" style={{ marginTop: 0 }}>
                                        A legacy PO covers one store on one ship date, so a revision edits that single delivery.
                                        Splitting across more dates means writing new POs.
                                    </p>
                                ) : (
                                    <p className="po-field-hint" style={{ marginTop: 0 }}>
                                        Each delivery date becomes its own purchase order (one PO per store per ship date) and gets its own column in the grid.
                                    </p>
                                )}
                                <div className="po-deliveries" style={{ marginTop: 'var(--space-2)' }}>
                                    {deliveries.map((d, i) => (
                                        <div key={i} className="po-delivery-row">
                                            <span className="po-delivery-tag">D{i + 1}</span>
                                            <input
                                                type="date"
                                                className="form-input"
                                                value={d}
                                                disabled={isRevision}
                                                onChange={e => setDeliveryDate(i, e.target.value)}
                                            />
                                            {deliveries.length > 1 && !isRevision && (
                                                <button className="btn btn-secondary po-delivery-remove" title="Remove this delivery" onClick={() => removeDelivery(i)}>✕</button>
                                            )}
                                        </div>
                                    ))}
                                    {!isRevision && (
                                        <button className="btn btn-secondary" onClick={addDelivery} disabled={deliveries.length >= MAX_DELIVERIES}>
                                            + Add delivery date
                                        </button>
                                    )}
                                </div>

                                <div className="po-generate">
                                    <button className="btn btn-primary" disabled style={{ width: '100%' }} title="Assisted suggestions are coming soon">
                                        ✨ Generate suggestions
                                    </button>
                                    <p className="po-field-hint">Coming soon — fills the colour/size grids from the enabled factors, in priority order.</p>
                                </div>
                            </aside>
                        </div>
                    )}
                </>
            )}
        </>
    )
}

// ── Suggestion factor list ───────────────────────────────────────────
// Priority order (top = weighed most) with per-factor on/off. Reorder by
// dragging the handle or with the keyboard-accessible arrows.
function FactorList({ factors, onChange }: { factors: Factor[]; onChange: (f: Factor[]) => void }) {
    const [dragKey, setDragKey] = useState<string | null>(null)
    const [overKey, setOverKey] = useState<string | null>(null)

    const move = (from: number, to: number) => {
        if (to < 0 || to >= factors.length || from === to) return
        const next = [...factors]
        const [moved] = next.splice(from, 1)
        next.splice(to, 0, moved)
        onChange(next)
    }

    const toggle = (key: string) =>
        onChange(factors.map(f => (f.key === key ? { ...f, enabled: !f.enabled } : f)))

    const enabledCount = factors.filter(f => f.enabled).length

    return (
        <>
            <div className="po-section-title">Decision factors ({enabledCount} on)</div>
            <p className="po-field-hint" style={{ marginTop: 0, marginBottom: 'var(--space-3)' }}>
                Drag or use the arrows to rank them — higher factors carry more weight.
            </p>
            <div className="po-factors">
                {factors.map((f, i) => (
                    <div
                        key={f.key}
                        className={[
                            'po-factor',
                            f.enabled ? '' : 'disabled',
                            f.available ? '' : 'unavailable',
                            dragKey === f.key ? 'dragging' : '',
                            overKey === f.key && dragKey !== f.key ? 'drop-target' : '',
                        ].filter(Boolean).join(' ')}
                        draggable
                        onDragStart={() => setDragKey(f.key)}
                        onDragEnd={() => { setDragKey(null); setOverKey(null) }}
                        onDragOver={e => { e.preventDefault(); setOverKey(f.key) }}
                        onDrop={e => {
                            e.preventDefault()
                            const from = factors.findIndex(x => x.key === dragKey)
                            if (from >= 0) move(from, i)
                            setDragKey(null); setOverKey(null)
                        }}
                    >
                        <span className="po-factor-rank" title="Drag to re-rank">
                            <span className="po-factor-number">{i + 1}</span>
                            <span aria-hidden>⁙</span>
                        </span>
                        <span>
                            <span className="po-factor-label">
                                {f.label}
                                {!f.available && <span className="po-factor-badge">Not yet</span>}
                            </span>
                            <span className="po-factor-hint">{f.hint}</span>
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                            <span className="po-factor-move">
                                <button type="button" title="Move up" disabled={i === 0} onClick={() => move(i, i - 1)}>▲</button>
                                <button type="button" title="Move down" disabled={i === factors.length - 1} onClick={() => move(i, i + 1)}>▼</button>
                            </span>
                            <label className="po-toggle small" title={f.available ? 'Enable this factor' : 'No data source connected yet'}>
                                <input
                                    type="checkbox"
                                    checked={f.enabled}
                                    disabled={!f.available}
                                    onChange={() => toggle(f.key)}
                                />
                                <span className="po-toggle-slider" />
                            </label>
                        </span>
                    </div>
                ))}
            </div>
        </>
    )
}

// ── Colour/size quantity grid ────────────────────────────────────────
// Rows are colours, columns are sizes — the shape a PO is actually written in
// (legacy PO lines are ItemCardID + ColorID + SizeID). Cell edits roll up to the
// row, the column, the delivery and finally the item row above.
function ItemVariantGrid({ item, itemIdx, deliveries, storeId, qtys, onQty, token }: {
    item: PoCatalogItem
    itemIdx: number
    deliveries: string[]
    storeId: string
    qtys: Record<number, Record<string, number>>
    onQty: (deliveryIdx: number, key: string, value: number) => void
    token: string | null
}) {
    const [activeDelivery, setActiveDelivery] = useState(0)
    const [showOnHand, setShowOnHand] = useState(false)

    // On-hand is the PO's store, so the axes are cached per (style, store).
    const axesQuery = useQuery<VariantAxes>({
        queryKey: ['po-variants', item.itemCardId, storeId],
        queryFn: () => api(
            `/api/purchase-orders/variants?itemCardId=${item.itemCardId}`
            + (storeId ? `&storeId=${encodeURIComponent(storeId)}` : ''), token),
        enabled: item.itemCardId != null,
    })

    if (item.itemCardId == null) {
        return <div className="po-grid-wrap"><p className="po-grid-note">This catalog line has no legacy item card, so it has no colour/size matrix.</p></div>
    }
    if (axesQuery.isLoading) {
        return <div className="po-grid-wrap"><p className="po-grid-note">Loading colours and sizes…</p></div>
    }
    if (axesQuery.isError) {
        return <div className="po-grid-wrap"><InlineError message="Failed to load colours and sizes for this style." /></div>
    }

    const axes = axesQuery.data
    const colors = axes?.colors ?? []
    const sizes = axes?.sizes ?? []
    if (colors.length === 0 || sizes.length === 0) {
        return <div className="po-grid-wrap"><p className="po-grid-note">No colour/size variants exist for this style in the legacy item file.</p></div>
    }

    const di = Math.min(activeDelivery, deliveries.length - 1)
    const cells = qtys[di] ?? {}
    const cellQty = (c: number, s: number) => cells[variantKey(c, s)] ?? 0
    const rowTotal = (c: number) => sizes.reduce((a, s) => a + cellQty(c, s.sizeId), 0)
    const colTotal = (s: number) => colors.reduce((a, c) => a + cellQty(c.colorId, s), 0)
    const gridTotal = colors.reduce((a, c) => a + rowTotal(c.colorId), 0)

    // Arrow keys walk the matrix, mirroring the POS variant picker.
    const cellId = (r: number, c: number) => `po-g${itemIdx}-d${di}-r${r}-c${c}`
    const onKeyDown = (e: KeyboardEvent<HTMLTableElement>) => {
        const delta: Record<string, [number, number]> = {
            ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1],
        }
        const d = delta[e.key]
        if (!d) return
        const m = (e.target as HTMLElement).id?.match(/^po-g\d+-d\d+-r(\d+)-c(\d+)$/)
        if (!m) return
        const next = document.getElementById(cellId(Number(m[1]) + d[0], Number(m[2]) + d[1]))
        if (next) { e.preventDefault(); next.focus() }
    }

    return (
        <div className="po-grid-wrap">
            <div className="po-grid-toolbar">
                {deliveries.length > 1 && (
                    <div className="po-grid-delivery-tabs">
                        {deliveries.map((d, i) => {
                            const units = Object.values(qtys[i] ?? {}).reduce((a, b) => a + b, 0)
                            return (
                                <button
                                    key={i}
                                    type="button"
                                    className={`po-grid-tab${i === di ? ' active' : ''}`}
                                    onClick={() => setActiveDelivery(i)}
                                >
                                    D{i + 1}{d ? ` · ${fmtDate(d)}` : ''}{units ? ` · ${units}` : ''}
                                </button>
                            )
                        })}
                    </div>
                )}
                <label className="po-toggle-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginTop: 0 }}>
                    <input type="checkbox" checked={showOnHand} onChange={e => setShowOnHand(e.target.checked)} />
                    Show on hand
                </label>
                <span style={{ flex: 1 }} />
                <span className="po-grid-note" style={{ margin: 0 }}>
                    {gridTotal.toLocaleString()} units on this delivery
                </span>
            </div>

            <div className="po-grid-scroll">
                <table className="po-cs-grid" onKeyDown={onKeyDown}>
                    <thead>
                        <tr>
                            <th className="po-cs-color" rowSpan={showOnHand ? 2 : 1}>Colour</th>
                            {sizes.map(s => (
                                <th key={s.sizeId} colSpan={showOnHand ? 2 : 1}>{s.name ?? '—'}</th>
                            ))}
                            <th className="po-cs-total" rowSpan={showOnHand ? 2 : 1}>Total</th>
                        </tr>
                        {showOnHand && (
                            <tr>
                                {sizes.map(s => (
                                    <Fragment key={s.sizeId}>
                                        <th className="po-cs-inv">OH</th>
                                        <th />
                                    </Fragment>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {colors.map((c, ci) => (
                            <tr key={c.colorId}>
                                <td className="po-cs-color">{c.name ?? '—'}</td>
                                {sizes.map((s, si) => {
                                    const key = variantKey(c.colorId, s.sizeId)
                                    const qty = cells[key] ?? 0
                                    const oh = axes?.onHand?.[key] ?? 0
                                    return (
                                        <Fragment key={key}>
                                            {showOnHand && (
                                                <td className={`po-cs-inv${oh === 0 ? ' zero' : ''}`}>{oh}</td>
                                            )}
                                            <td className="po-cs-qty">
                                                <input
                                                    id={cellId(ci, si)}
                                                    inputMode="numeric"
                                                    className={qty ? 'filled' : ''}
                                                    value={qty || ''}
                                                    placeholder="0"
                                                    onFocus={e => { const el = e.currentTarget; requestAnimationFrame(() => el.select()) }}
                                                    onChange={e => {
                                                        const digits = e.target.value.replace(/[^0-9]/g, '')
                                                        onQty(di, key, digits ? parseInt(digits, 10) : 0)
                                                    }}
                                                />
                                            </td>
                                        </Fragment>
                                    )
                                })}
                                <td className="po-cs-total">{rowTotal(c.colorId) || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td className="po-cs-color">Total</td>
                            {sizes.map(s => (
                                <Fragment key={s.sizeId}>
                                    {showOnHand && <td className="po-cs-inv" />}
                                    <td>{colTotal(s.sizeId) || '—'}</td>
                                </Fragment>
                            ))}
                            <td className="po-cs-total">{gridTotal || '—'}</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
            <p className="po-grid-note">
                Quantities are stored per colour and size — the level a purchase order line actually records.
                Row, column and delivery totals roll up into the style row above.
            </p>
        </div>
    )
}
